/**
 * AdminDemoTab (Slice C+)
 * =======================
 * Admin dashboard tab for the demo daily-limit feature. Two panels:
 *   1. Demo limit config editor — admin-tunable numbers (daily limit, first-day
 *      limit, demo duration, reactivation), no deploy. env default vs DB
 *      override is shown per field; a field can be reset to its env default.
 *   2. Demo→PRO conversion funnel — read-only weekly table so we can watch
 *      limit-hit → sales-inquiry and judge whether e.g. 10 vs 5/day converts.
 *
 * Mirrors the existing admin-tab conventions (adminService + authenticatedFetch,
 * .admin-* styling, inline success/error alerts).
 */
import { useEffect, useState } from 'preact/hooks';
import {
  getDemoConfig,
  updateDemoConfig,
  getDemoConversionsFunnel,
} from '../../services/adminService.js';
import { IonTierLimitsPanel } from './IonTierLimitsPanel.jsx';
import { ionHidden } from '../../state/chatStore.js';
import { parsePlotBinGranularityDraft } from './adminDemoConfigHelpers.js';

// The four editable integer fields (order = display order).
const CONFIG_FIELDS = [
  {
    key: 'daily_limit_demo',
    label: 'Daily deep-dive limit (demo)',
    help: 'Distinct cell detail views a demo user gets per day after the first 48h.',
  },
  {
    key: 'first_day_limit',
    label: 'First-48h limit',
    help: 'Higher allowance during a new demo user’s first 48 hours (before the daily limit above applies).',
  },
  {
    key: 'demo_duration_days',
    label: 'Demo duration (days)',
    help: 'How long a demo stays active after approval.',
  },
  {
    key: 'demo_reactivation_days',
    label: 'Reactivation window (days)',
    help: 'Cooldown before an expired demo can be reactivated.',
  },
];

const FUNNEL_COLUMNS = [
  { key: 'limit_hit', label: 'Limit hit' },
  { key: 'export_blocked', label: 'Exports blocked' },
  { key: 'sales_inquiry', label: 'Sales inquiry' },
  { key: 'quote_sent', label: 'Quote sent' },
  { key: 'won', label: 'Won' },
];

export function AdminDemoTab() {
  // ── Panel 1: config ───────────────────────────────────────────────
  const [config, setConfig] = useState(null); // { <field>:int, overrides:{} }
  const [drafts, setDrafts] = useState({}); // in-progress edits (string values)
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [savingField, setSavingField] = useState(null);
  const [configError, setConfigError] = useState('');
  const [configSuccess, setConfigSuccess] = useState('');

  // ── Panel 1c: anti-scraping controls (strict_mode + plot_bin_granularity) ─
  // DB-only knobs (no env fallback), so not part of `overrides` — saved together
  // via one PUT (see B7 brief: "toggle + save → payload has both fields").
  const [strictDraft, setStrictDraft] = useState(false);
  const [binDraftText, setBinDraftText] = useState('{}');
  const [binDraftError, setBinDraftError] = useState('');
  const [savingAntiScrape, setSavingAntiScrape] = useState(false);

  // ── Panel 2: funnel ───────────────────────────────────────────────
  const [funnel, setFunnel] = useState(null);
  const [loadingFunnel, setLoadingFunnel] = useState(true);
  const [funnelError, setFunnelError] = useState('');

  useEffect(() => {
    loadConfig();
    loadFunnel();
  }, []);

  function applyConfig(data) {
    setConfig(data);
    // Seed drafts from the effective values so inputs are controlled.
    const seeded = {};
    CONFIG_FIELDS.forEach((f) => {
      seeded[f.key] = data?.[f.key] != null ? String(data[f.key]) : '';
    });
    setDrafts(seeded);
    setStrictDraft(Boolean(data?.strict_mode));
    setBinDraftText(JSON.stringify(data?.plot_bin_granularity ?? {}, null, 2));
    setBinDraftError('');
  }

  async function loadConfig() {
    setLoadingConfig(true);
    setConfigError('');
    try {
      applyConfig(await getDemoConfig());
    } catch (err) {
      console.error('[AdminDemo] Load config error:', err);
      setConfigError(err?.message || 'Failed to load demo config');
    } finally {
      setLoadingConfig(false);
    }
  }

  async function loadFunnel() {
    setLoadingFunnel(true);
    setFunnelError('');
    try {
      setFunnel(await getDemoConversionsFunnel({ days: 42 }));
    } catch (err) {
      console.error('[AdminDemo] Load funnel error:', err);
      setFunnelError(err?.message || 'Failed to load conversion funnel');
    } finally {
      setLoadingFunnel(false);
    }
  }

  function flashSuccess(msg) {
    setConfigSuccess(msg);
    setTimeout(() => setConfigSuccess(''), 4000);
  }

  async function saveField(key) {
    const raw = String(drafts[key] ?? '').trim();
    const value = parseInt(raw, 10);
    if (!Number.isFinite(value) || value < 1 || value > 1000) {
      setConfigError('Enter a whole number between 1 and 1000.');
      return;
    }
    setConfigError('');
    setSavingField(key);
    try {
      // Send ONLY the changed field.
      const next = await updateDemoConfig({ [key]: value });
      applyConfig(next);
      flashSuccess('Saved. Applies across the app within ~1 minute.');
    } catch (err) {
      console.error('[AdminDemo] Save field error:', err);
      setConfigError(err?.message || 'Failed to save');
    } finally {
      setSavingField(null);
    }
  }

  async function resetField(key) {
    setConfigError('');
    setSavingField(key);
    try {
      // Sending null clears the DB override → reverts to the env default.
      const next = await updateDemoConfig({ [key]: null });
      applyConfig(next);
      flashSuccess('Reverted to default. Applies within ~1 minute.');
    } catch (err) {
      console.error('[AdminDemo] Reset field error:', err);
      setConfigError(err?.message || 'Failed to reset');
    } finally {
      setSavingField(null);
    }
  }

  function onBinDraftChange(text) {
    setBinDraftText(text);
    const { error } = parsePlotBinGranularityDraft(text);
    setBinDraftError(error || '');
  }

  async function saveAntiScrapeSettings() {
    const { value: parsedBins, error } = parsePlotBinGranularityDraft(binDraftText);
    if (error) {
      setBinDraftError(error);
      return;
    }
    setConfigError('');
    setSavingAntiScrape(true);
    try {
      const next = await updateDemoConfig({
        strict_mode: strictDraft,
        plot_bin_granularity: parsedBins,
      });
      applyConfig(next);
      flashSuccess('Saved. Applies across the app within ~1 minute.');
    } catch (err) {
      console.error('[AdminDemo] Save anti-scrape settings error:', err);
      setConfigError(err?.message || 'Failed to save');
    } finally {
      setSavingAntiScrape(false);
    }
  }

  const overrides = config?.overrides || {};
  const antiScrapeDirty =
    !!config &&
    (strictDraft !== Boolean(config.strict_mode) ||
      binDraftText.trim() !== JSON.stringify(config.plot_bin_granularity ?? {}, null, 2).trim());

  return (
    <div className="admin-tab-content">
      {/* ── Panel 1: Config editor ─────────────────────────────── */}
      <div className="admin-section-header">
        <h2>Demo Settings</h2>
        <button
          className="admin-btn admin-btn--secondary"
          onClick={loadConfig}
          disabled={loadingConfig}
        >
          Refresh
        </button>
      </div>

      <p className="admin-demo-hint">
        Tune the demo daily limits and durations live — no deploy needed. Changes
        take effect within about a minute; an already-open demo session picks them
        up when its tab regains focus (or on reload). A field showing{' '}
        <span className="admin-demo-badge admin-demo-badge--override">overridden</span>{' '}
        uses this value; <span className="admin-demo-badge">default</span> falls
        back to the environment default.
      </p>

      {configSuccess && (
        <div className="admin-alert admin-alert--success">{configSuccess}</div>
      )}
      {configError && (
        <div className="admin-alert admin-alert--error">{configError}</div>
      )}

      {loadingConfig ? (
        <div className="admin-loading">
          <div className="admin-spinner"></div>
          <p>Loading demo config…</p>
        </div>
      ) : config ? (
        <div className="admin-demo-config-grid">
          {CONFIG_FIELDS.map((field) => {
            const isOverride = overrides[field.key] === true;
            const busy = savingField === field.key;
            const draftVal = drafts[field.key] ?? '';
            const dirty = String(draftVal) !== String(config[field.key] ?? '');
            return (
              <div className="admin-demo-config-row" key={field.key}>
                <div className="admin-demo-config-labels">
                  <label
                    className="admin-form-label admin-demo-config-label"
                    htmlFor={`demo-cfg-${field.key}`}
                  >
                    {field.label}
                    <span
                      className={`admin-demo-badge${isOverride ? ' admin-demo-badge--override' : ''}`}
                    >
                      {isOverride ? 'overridden' : 'default'}
                    </span>
                  </label>
                  <p className="admin-demo-config-help">{field.help}</p>
                </div>
                <div className="admin-demo-config-controls">
                  <input
                    id={`demo-cfg-${field.key}`}
                    className="admin-demo-input"
                    type="number"
                    min="1"
                    max="1000"
                    step="1"
                    value={draftVal}
                    disabled={busy}
                    onInput={(e) =>
                      setDrafts({ ...drafts, [field.key]: e.target.value })
                    }
                  />
                  <button
                    className="admin-btn admin-btn--primary"
                    onClick={() => saveField(field.key)}
                    disabled={busy || !dirty}
                  >
                    {busy ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    className="admin-btn admin-btn--secondary"
                    onClick={() => resetField(field.key)}
                    disabled={busy || !isOverride}
                    title={
                      isOverride
                        ? 'Clear the override and revert to the environment default'
                        : 'Already using the environment default'
                    }
                  >
                    Reset to default
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      {/* ── Panel 1a: Anti-scraping controls (strict_mode + plot bins) ── */}
      {config && (
        <div className="admin-demo-antiscrape-section">
          <h3 className="admin-demo-antiscrape-heading">Anti-scraping controls</h3>
          <label className="admin-demo-checkbox">
            <input
              type="checkbox"
              checked={strictDraft}
              disabled={savingAntiScrape}
              onChange={(e) => setStrictDraft(e.target.checked)}
            />
            Strict plot anonymization
          </label>
          <p className="admin-demo-config-help">
            Also bins the free axis in premium-metric plot views; currently a
            stronger anti-scrape mode.
          </p>

          <label
            className="admin-form-label admin-demo-config-label"
            htmlFor="demo-cfg-plot-bin-granularity"
          >
            Plot bin granularity (per metric)
          </label>
          <p className="admin-demo-config-help">
            JSON object of metric key → bin width. The server enforces a floor
            per metric (bins may only be made coarser, never finer than the
            vetted default) — a value below the floor is rejected on save.
          </p>
          <textarea
            id="demo-cfg-plot-bin-granularity"
            className="admin-demo-textarea"
            rows={8}
            disabled={savingAntiScrape}
            value={binDraftText}
            onInput={(e) => onBinDraftChange(e.target.value)}
          />
          {binDraftError && (
            <p className="admin-demo-bin-error">{binDraftError}</p>
          )}

          <button
            className="admin-btn admin-btn--primary"
            onClick={saveAntiScrapeSettings}
            disabled={savingAntiScrape || !antiScrapeDirty || !!binDraftError}
          >
            {savingAntiScrape ? 'Saving…' : 'Save anti-scraping settings'}
          </button>
        </div>
      )}

      {/* ── Panel 1b: Ion (AI chat) demo limits ────────────────── */}
      {/* BA wants the demo-tier Ion limits under the Demo tab. The pro-tier
          limits live in AI Management → Tier limits. daily_usd/weekly_usd
          are the soft cost budgets — the sole enforcement mechanism since
          the request-count cap was removed 2026-07-17. Shares
          IonTierLimitsPanel so the two surfaces can't drift. */}
      {!ionHidden.value && (
        <div className="admin-demo-ion-section">
          <IonTierLimitsPanel
            tier="demo"
            heading="Ion (AI chat) — demo limits"
            fields={['daily_usd', 'weekly_usd']}
          />
        </div>
      )}

      {/* ── Panel 2: Conversion funnel ─────────────────────────── */}
      <div className="admin-section-header admin-demo-funnel-header">
        <h2>Demo → PRO conversion (last 42 days)</h2>
        <button
          className="admin-btn admin-btn--secondary"
          onClick={loadFunnel}
          disabled={loadingFunnel}
        >
          Refresh
        </button>
      </div>

      {funnelError && (
        <div className="admin-alert admin-alert--error">{funnelError}</div>
      )}

      {loadingFunnel ? (
        <div className="admin-loading">
          <div className="admin-spinner"></div>
          <p>Loading conversion funnel…</p>
        </div>
      ) : funnel ? (
        <>
          <div className="admin-table-container">
            <table className="admin-table admin-demo-funnel-table">
              <thead>
                <tr>
                  <th>Week of</th>
                  {FUNNEL_COLUMNS.map((c) => (
                    <th key={c.key}>{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(funnel.weeks || []).length === 0 ? (
                  <tr>
                    <td colSpan={FUNNEL_COLUMNS.length + 1}>
                      No conversion events in this period yet.
                    </td>
                  </tr>
                ) : (
                  (funnel.weeks || []).map((w) => (
                    <tr key={w.week_start}>
                      <td>{w.week_start}</td>
                      {FUNNEL_COLUMNS.map((c) => (
                        <td key={c.key}>{w[c.key] ?? 0}</td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
              {funnel.totals && (
                <tfoot>
                  <tr className="admin-demo-funnel-totals">
                    <td>Total</td>
                    {FUNNEL_COLUMNS.map((c) => (
                      <td key={c.key}>{funnel.totals[c.key] ?? 0}</td>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          <p className="admin-demo-funnel-caption">
            Weekly counts. <strong>Limit hit → Sales inquiry</strong> is the
            primary conversion signal to watch (adjust the daily limit above and
            compare). <em>Exports blocked</em> counts demo attempts to export a
            plot, radar chart, or ranking — a second intent signal alongside the
            daily limit. <em>Quote sent</em> and <em>Won</em> are 0 for now — they
            will be derived from subscription history in a fast-follow.
          </p>
        </>
      ) : null}
    </div>
  );
}
